/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Users, CalendarDays, TrendingUp, Clock } from 'lucide-react';
import { useAdmin } from './AdminAuth';

const MetricCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6">
    <div className="w-11 h-11 rounded-xl bg-[#0e9f6e]/12 flex items-center justify-center mb-4">
      <Icon size={20} className="text-[#0e9f6e]" />
    </div>
    <p className="font-display text-3xl font-700 text-[#0f1712]">{value}</p>
    <p className="text-sm text-[#0f1712]/50 mt-1">{label}</p>
    {sub && <p className="text-xs text-[#0f1712]/40 mt-2">{sub}</p>}
  </div>
);

const StatsPanel = () => {
  const { authAxios } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/admin/stats');
      setStats(res.data);
    } catch (e) { /* handled by interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-10 text-center text-[#0f1712]/40 text-sm">Loading stats…</div>;
  if (!stats) return <div className="p-10 text-center text-[#0f1712]/40 text-sm">No data.</div>;

  const maxDaily = Math.max(1, ...stats.daily.map((d) => d.count));
  const latestStr = stats.latest ? new Date(stats.latest).toLocaleString() : '—';

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-[#0f1712]">Overview</h2>
        <p className="text-[#0f1712]/50 text-sm mt-1">A quick pulse on your FLIX sign-ups.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={Users} label="Total sign-ups" value={stats.total} />
        <MetricCard icon={CalendarDays} label="Today" value={stats.today} />
        <MetricCard icon={TrendingUp} label="Last 7 days" value={stats.last7days} />
        <MetricCard icon={Clock} label="Latest sign-up" value={stats.today > 0 || stats.total > 0 ? '✓' : '—'} sub={latestStr} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-[#0f1712]/10 bg-white p-6">
          <h3 className="font-display text-lg font-700 text-[#0f1712] mb-6">Sign-ups · last 7 days</h3>
          <div className="flex items-end justify-between gap-3 h-52">
            {stats.daily.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <span className="text-xs font-semibold text-[#0f1712]/60">{d.count}</span>
                <div
                  className="w-full rounded-t-lg bg-[#0e9f6e] transition-all duration-500"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? '8px' : '3px', opacity: d.count > 0 ? 1 : 0.25 }}
                />
                <span className="text-xs text-[#0f1712]/40">{d.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#0f1712]/10 bg-white p-6">
          <h3 className="font-display text-lg font-700 text-[#0f1712] mb-6">By source</h3>
          {stats.by_source.length === 0 ? (
            <p className="text-sm text-[#0f1712]/40">No sign-ups yet.</p>
          ) : (
            <div className="space-y-4">
              {stats.by_source.map((s, i) => {
                const pct = stats.total > 0 ? Math.round((s.count / stats.total) * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-[#0f1712] font-medium">{s.source}</span>
                      <span className="text-[#0f1712]/50">{s.count} · {pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#f6f6f2] overflow-hidden">
                      <div className="h-full rounded-full bg-[#0e9f6e]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsPanel;
